/**
 * MBA Lab — rules-based checks for a drafted peer evaluation.
 *
 * Four new, original checks live here; the two genuinely shared ones
 * (hasObservable, noAbsolutes) are imported from message-checks.js rather than
 * re-implemented, along with its lower/containsPhrase/containsAny helpers. Same
 * spirit as every other validator on this site: plain text heuristics, no AI
 * call, no external service.
 *
 * The four new ones are the failure modes
 * Content/Blog/peer-evaluations-mba-teams.md names specifically, in the order
 * that article raises them:
 *
 *   noCharacterVerdict  - "'Unreliable' is a character judgment. It's
 *                          contestable, it's insulting, and there's nothing
 *                          actionable in it."
 *   noInferredMotive    - "You saw a missed deadline. You did not see 'didn't
 *                          care.'"
 *   noVagueSuperlatives - "'Great teammate' tells nobody anything."
 *   mentionsTeamImpact  - "Name the consequence. Feedback without cost attached
 *                          is easy to file away as someone's preference."
 *
 * Display rule for the consuming UI: per-field passes only. There is no
 * aggregate count and no score anywhere in this module, deliberately. A
 * "7/10 feedback quality" badge would invite writing to the checker instead of
 * describing what actually happened, which is the whole thing being avoided.
 */

import {
  hasObservable,
  noAbsolutes,
  lower,
  containsPhrase,
  containsAny,
  CHECK_LABELS as SHARED_CHECK_LABELS,
} from './message-checks.js';

function toText(value) {
  return typeof value === 'string' ? value : '';
}

/**
 * Trait verdicts about the person. "unreliable" is the article's own example
 * of the thing to avoid, so it leads the list.
 *
 * "difficult" appears only in its verdict phrasings, not bare: "the
 * reconciliation was difficult" and "a difficult data set" are ordinary,
 * accurate descriptions of work, and flagging them would train people to stop
 * describing the work honestly. Same reasoning keeps bare "negative" and bare
 * "slow" out entirely.
 */
const CHARACTER_VERDICTS = Object.freeze([
  'unreliable',
  'lazy',
  'unprofessional',
  'not a team player',
  'bad attitude',
  'difficult to work with',
  'a difficult person',
  'difficult teammate',
  'incompetent',
  'careless',
  'selfish',
  'arrogant',
  'flaky',
  'disorganized',
  'toxic',
  'checked out',
  'dead weight',
]);

/**
 * Claims about why someone acted, which nobody in a group project is ever in a
 * position to observe. Curly apostrophes are normalized by lower() before these
 * are matched, so each contraction only needs its straight-quote spelling here.
 */
const INFERRED_MOTIVES = Object.freeze([
  "didn't care",
  "doesn't care",
  "did not care",
  'deliberately',
  'on purpose',
  'intentionally',
  "couldn't be bothered",
  "could not be bothered",
  "didn't bother",
  "clearly didn't want to",
  "didn't want to",
  'chose not to',
  "wasn't interested",
  'no interest in',
]);

/** Contentless praise. The article's complaint is that these tell nobody
 * anything, not that enthusiasm is forbidden - see noVagueSuperlatives. */
const VAGUE_SUPERLATIVES = Object.freeze([
  'great teammate',
  'great to work with',
  'awesome',
  'amazing',
  'rock star',
  'rockstar',
  'superstar',
  'team player',
  'fantastic',
  'incredible',
  'a pleasure to work with',
  'excellent teammate',
  'best teammate',
]);

/**
 * Ways of naming an effect on the team or the deliverable rather than on your
 * own patience. Concrete artefacts ("the deck", "the model") count because the
 * article's own worked example is written entirely in them.
 */
// 'slide'/'slides' and 'analysis' were removed after review found them firing on plain topic
// mentions with no stated effect ("the analysis he sent me was hard to follow", "he mentioned
// the slides a couple of times") - both are generic enough words to show up in an unrelated
// sentence far more often than the other, more specifically-scoped markers below.
const TEAM_IMPACT_MARKERS = Object.freeze([
  'the deck',
  'our deck',
  'the deadline',
  'our deadline',
  'the team',
  'our team',
  'we had to',
  'we ended up',
  'the rest of us',
  'rework',
  'redo',
  'the model',
  'our model',
  'deliverable',
  'the submission',
  'our submission',
  'the presentation',
  'the report',
  'the write-up',
  'q&a',
  'integrate',
  'integration',
  'our grade',
]);

/** Passes when the text describes behaviour rather than delivering a verdict on
 * the person. */
export function noCharacterVerdict(text) {
  return !containsAny(lower(text), CHARACTER_VERDICTS);
}

/** Passes when the text stops at what was observable and doesn't assert why the
 * person did it. */
export function noInferredMotive(text) {
  return !containsAny(lower(text), INFERRED_MOTIVES);
}

/**
 * Passes when praise isn't purely contentless. A superlative on its own fails;
 * the same superlative alongside something concrete and datable passes, because
 * "Aisha was amazing - on Tuesday she rebuilt the reconciliation overnight" is
 * not the failure the article is describing. That composition is the point: the
 * article's objection is to praise that is *unbacked*, not to warm words.
 *
 * "not a team player" is excluded from the "team player" marker: that phrase is
 * a character verdict (noCharacterVerdict already catches it), and firing a
 * vague-praise warning on a criticism would just be wrong.
 */
export function noVagueSuperlatives(text) {
  const t = lower(text);
  // "not a team player" is a character verdict (noCharacterVerdict's job), so every negated
  // occurrence is blanked out (with a space, so unrelated words don't get glued together)
  // before testing for a bare "team player" - a whole-text substring check for the negated
  // phrase would wrongly clear a SEPARATE, genuinely unbacked "team player" praise elsewhere
  // in the same text just because a negated mention appears anywhere in it.
  const withoutNegatedTeamPlayer = t.replace(/not a team player/g, ' ');
  const hit = VAGUE_SUPERLATIVES.some((phrase) => {
    if (phrase === 'team player') return containsPhrase(withoutNegatedTeamPlayer, phrase);
    return containsPhrase(t, phrase);
  });
  if (!hit) return true;
  return hasObservable(text);
}

/** Passes when the text names an effect on the team or the deliverable, not
 * just on the writer. */
export function mentionsTeamImpact(text) {
  if (!toText(text).trim()) return false;
  return containsAny(lower(text), TEAM_IMPACT_MARKERS);
}

export const CHECKS = Object.freeze({
  hasObservable,
  noAbsolutes,
  noCharacterVerdict,
  noInferredMotive,
  noVagueSuperlatives,
  mentionsTeamImpact,
});

/** Labels for the two shared checks are re-used from message-checks.js rather
 * than re-typed, so the same check can never read as two different things on
 * two different tools. */
export const CHECK_LABELS = Object.freeze({
  hasObservable: SHARED_CHECK_LABELS.hasObservable,
  noAbsolutes: SHARED_CHECK_LABELS.noAbsolutes,
  noCharacterVerdict: 'Describes behavior, not a verdict on the person',
  noInferredMotive: 'Stops at what you saw, not why they did it',
  noVagueSuperlatives: 'Praise is backed by something specific',
  mentionsTeamImpact: 'Names an effect on the team or the deliverable',
});

/**
 * Which checks apply to which field. Each field gets the two or three that are
 * actually meaningful for what it is asking - running all six everywhere would
 * bury the one that matters.
 *
 * gapAlternative gets hasObservable because the article's own model answer for
 * it ("Flagging on Sunday that Tuesday would slip") is specific and datable:
 * a vague alternative is no more useful than a vague criticism.
 */
export const FIELD_CHECK_IDS = Object.freeze({
  strengthObservation: Object.freeze(['hasObservable', 'noVagueSuperlatives', 'noAbsolutes']),
  strengthImpact: Object.freeze(['mentionsTeamImpact', 'noVagueSuperlatives']),
  gapObservation: Object.freeze(['hasObservable', 'noCharacterVerdict', 'noInferredMotive', 'noAbsolutes']),
  gapCost: Object.freeze(['mentionsTeamImpact', 'noCharacterVerdict']),
  gapAlternative: Object.freeze(['hasObservable', 'noInferredMotive']),
});

export function checkIdsFor(field) {
  return FIELD_CHECK_IDS[field] ?? [];
}

/**
 * Runs a named subset of checks against one field's text and returns
 * {id, label, passed} for each - the shape the UI renders directly.
 *
 * Empty array for blank text, exactly as message-checks.js's runChecks does and
 * for the same reason: four of the six here are "does NOT contain X" shaped and
 * are vacuously true on an empty string, so an untouched textarea would
 * otherwise display a row of green passes before anyone had typed anything.
 */
export function runFieldChecks(text, checkIds) {
  if (!toText(text).trim()) return [];
  return (checkIds ?? [])
    .filter((id) => typeof CHECKS[id] === 'function')
    .map((id) => ({ id, label: CHECK_LABELS[id], passed: CHECKS[id](text) }));
}
